import { gatewayApi as api } from './gatewayApi';

// Operation Types
export interface Operation {
  Id?: string;
  OperationNum: number;
  Name: string;
  Description?: string;
  PartId: string;
  OperationTypeId: string;
  MachineId: string;
  ApprovalStampReqd: boolean;
  PassOffReqd: boolean;
  Active: boolean;
  CreatedDate?: string;
  UpdatedDate?: string;
  // Related entities
  Part?: Part;
  OperationType?: OperationType;
  Machine?: Machine;
  ToolFixtures?: ToolFixture[];
  WorkInstructions?: WorkInstruction[];
  Inputs?: OperationInput[];
}

export interface CreateOperationRequest {
  OperationNum: number;
  Name: string;
  Description?: string;
  PartId: string;
  OperationTypeId: string;
  MachineId: string;
  ApprovalStampReqd: boolean;
  PassOffReqd: boolean;
  Active: boolean;
  ToolFixtures?: { Id: string }[];
  WorkInstructions?: { Id: string }[];
  Inputs?: { Id: string }[];
}

export interface UpdateOperationRequest extends Partial<CreateOperationRequest> {
  Id: string;
}

// Related Entity Types
export interface OperationType {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

export interface Machine {
  Id: string;
  Name: string;
  Description?: string;
  MachineNum?: string;
  Active: boolean;
}

export interface Part {
  Id: string;
  Number: string;
  Description?: string;
  Name?: string;
  Active: boolean;
}

export interface ToolFixture {
  Id: string;
  TFNum: string;
  Description: string;
  Name?: string;
  Active: boolean;
}

export interface WorkInstruction {
  Id: string;
  Name: string;
  Description: string;
  Active: boolean;
}

export interface OperationInput {
  Id: string;
  Name: string;
  Description?: string;
  Active: boolean;
}

// Response Types
export interface OperationsResponse {
  data: Operation[];
  total: number;
  page: number;
  limit: number;
}

export interface OperationStatistics {
  total: number;
  active: number;
  inactive: number;
  byOperationType: Record<string, number>;
  byMachine: Record<string, number>;
}

// Operations API Service
export const operationsApi = {
  // Operation CRUD operations
  getAll: (params?: { 
    page?: number; 
    limit?: number; 
    search?: string; 
    active?: boolean;
    operationTypeId?: string;
    machineId?: string;
    partId?: string;
  }): Promise<OperationsResponse> =>
    api.get<OperationsResponse>('/operation', params),

  getById: (id: string): Promise<Operation> =>
    api.get<Operation>(`/operation/${id}`),

  create: (operation: CreateOperationRequest): Promise<Operation> =>
    api.post<Operation>('/operation', operation),

  update: (id: string, operation: UpdateOperationRequest): Promise<Operation> =>
    api.put<Operation>(`/operation/${id}`, operation),

  delete: (id: string): Promise<void> =>
    api.delete<void>(`/operation/${id}`),

  search: (filters: Record<string, any>): Promise<Operation[]> =>
    api.post<Operation[]>('/operation/search', { filter: filters }),

  // Custom API endpoints from Angular version
  getOperationType: (operationId: string): Promise<OperationType> =>
    api.get<OperationType>(`/operation/${operationId}/type`),

  getOperationTfc: (operationId: string): Promise<ToolFixture[]> =>
    api.get<ToolFixture[]>(`/operation/${operationId}/tfc`),

  getOperationInputs: (operationId: string): Promise<OperationInput[]> =>
    api.get<OperationInput[]>(`/operation/${operationId}/inputs`),

  // Related entities
  getOperationTypes: (): Promise<OperationType[]> =>
    api.get<OperationType[]>('/operationtype'),

  getMachines: (): Promise<Machine[]> =>
    api.get<Machine[]>('/machine'),

  getParts: (): Promise<Part[]> =>
    api.get<Part[]>('/part'),

  getToolFixtures: (): Promise<ToolFixture[]> =>
    api.get<ToolFixture[]>('/toolfixture'),

  getWorkInstructions: (): Promise<WorkInstruction[]> =>
    api.get<WorkInstruction[]>('/workinstruction'),

  getInputs: (): Promise<OperationInput[]> =>
    api.get<OperationInput[]>('/inputs'),

  // Statistics
  getStatistics: (): Promise<OperationStatistics> =>
    api.get<OperationStatistics>('/operation/statistics'),

  // Bulk operations
  bulkActivate: (operationIds: string[]): Promise<void> =>
    api.post<void>('/operation/bulk/activate', { ids: operationIds }),

  bulkDeactivate: (operationIds: string[]): Promise<void> =>
    api.post<void>('/operation/bulk/deactivate', { ids: operationIds }),

  // Export
  export: (): Promise<Blob> =>
    api.getBlob('/operation/export'),

  // Validation helpers
  validation: {
    validateOperation: (operation: Partial<CreateOperationRequest> | Partial<UpdateOperationRequest> | Partial<Operation>): string[] => {
      const errors: string[] = [];
      
      if (!operation.OperationNum || operation.OperationNum <= 0) {
        errors.push('Operation Number is required and must be greater than 0');
      }
      
      if (!operation.Name?.trim()) {
        errors.push('Operation Name is required');
      }
      
      if (!operation.PartId) {
        errors.push('Part is required');
      }
      
      if (!operation.OperationTypeId) {
        errors.push('Operation Type is required');
      }
      
      if (!operation.MachineId) {
        errors.push('Machine is required');
      }
      
      return errors;
    },
    validateBeforeSave: (payload: CreateOperationRequest | UpdateOperationRequest): CreateOperationRequest | UpdateOperationRequest => {
      // Normalize nested arrays to only Ids if needed; keep payload otherwise
      const normalized: any = { ...payload };
      if ((payload as any).ToolFixtures && Array.isArray((payload as any).ToolFixtures)) {
        normalized.ToolFixtures = (payload as any).ToolFixtures.map((tf: any) => ({ Id: tf.Id || tf }));
      }
      if ((payload as any).WorkInstructions && Array.isArray((payload as any).WorkInstructions)) {
        normalized.WorkInstructions = (payload as any).WorkInstructions.map((wi: any) => ({ Id: wi.Id || wi }));
      }
      if ((payload as any).Inputs && Array.isArray((payload as any).Inputs)) {
        normalized.Inputs = (payload as any).Inputs.map((inp: any) => ({ Id: inp.Id || inp }));
      }
      return normalized;
    },
  },
}; 